import { AuthHelperService } from './../../services/auth-helper.service';
import { Component, OnInit, Input, SimpleChanges, OnChanges, HostListener, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { IntralinksFile } from '../../tamalelibs/models/intralinks.model';
import { ColDef, GridOptions } from 'ag-grid-community';
import { DEFAULT_PAGE_SIZE } from '../../widgets/grid/grid.model';
import { IntralinksFolderActionsComponent } from './intralinks-folder-actions.component';
import { IntralinksFolderNameComponent } from './intralinks-folder-name.component';
import { DatePipe } from '@angular/common';
import { FileHelperService } from '../../services/file-helper.service';
import { AppConfig } from '../../tamalelibs/models/app-config.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IntralinksService } from '../../tamalelibs/services/intralinks.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { NoteDialogOpenOptions, NoteDialogType } from '../note-dialog/note-dialog.model';
import { NoteDialogService } from '../note-dialog/note-dialog.service';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';


@Component({
    selector: 'tam-intralinks-folder',
    templateUrl: './intralinks-folder.component.html',
    styleUrls: ['./intralinks-folder.component.scss']
}

) export class IntralinksFolderComponent implements OnInit, OnChanges, OnDestroy {

    @Input() loading: boolean;
    @Input() files: IntralinksFile[];
    @Input() autoHeight?: boolean;
    @Input() isgroup: boolean;
    columnDefs: Array<ColDef>;
    contextMenuConfig: ContextMenuConfig;
    gridOptions: GridOptions;
    selectedItemMap: Map<string, number> = new Map();
    allSelectedFiles: Map<string, IntralinksFile[]> = new Map(); // Gets all selected files in all ag-grids
    selectedIndexes: Set<number> = new Set();
    selectedIndex = 0;
    noDataStyle = 'none';
    minIndex: number;
    maxIndex: number;

    private _gridApi;
    private _columnApi;
    private _screenId = '';
    private _ngUnsubscribe: Subscription;
    private _allowSubmitFiles: IntralinksFile[];
    private _selectedFiles: IntralinksFile[] = [];

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _noteDialogService: NoteDialogService,
        private _fileHelperService: FileHelperService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _authHelperService: AuthHelperService,
        private _intralinkService: IntralinksService,

    ) { }

    ngOnInit() {
        this._initContextMenu();
        this._initGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this._gridApi) {
            this._gridApi.setRowData(changes.files.currentValue);
        } else {
            this._initGrid();
        }
        this._screenId = this._activatedRoute.snapshot.params['id'];
    }

    ngOnDestroy() {
        if (this._ngUnsubscribe) {
            this._ngUnsubscribe.unsubscribe();
        }
        this.contextMenuConfig.events$.unsubscribe();
        // It is necessary to empty the selected folder when the component is destroyed, solving the problem that causes the previously selected files to persist when switching filter conditions
        if (this.files && this.files.length > 0) {
            this._intralinkService.updateSelectedFiles([], this.files[0].parentId);
        }
    }

    suppressDefaultContextMenu(event) {
        // agGrid bug, we need to handle this
        event.preventDefault();
        event.stopPropagation();
    }

    onContextMenu(event) {
        const index = event.rowIndex;
        if (!(this.selectedIndexes.has(index))) {
            this._changeSelect(index);
        }
        // Set the selected node to the selected state in the agGrid
        this._changeSelectStyle();
        // Get the selected files in the agGrid
        this._selectedFiles = this.gridOptions.api.getSelectedRows();
        if (this.isgroup) {
            this.allSelectedFiles = this._intralinkService.updateSelectedFiles(this._selectedFiles, this.files[0].parentId);
            const selectedFiles = [];
            this.allSelectedFiles.forEach((value, key) => {
                if (value.length !== 0) {
                    for (const file of value) {
                        selectedFiles.push(file);
                    }
                }
            });
            this._selectedFiles = selectedFiles;
        }

        this.contextMenuConfig.items = [];
        // To limit only Undeposited documents can be submitted
        this._allowSubmitFiles = this._selectedFiles.filter(item => {
            return (item.entityType === 'DOCUMENT' && !(item.isSubmitted));
        });
        if (this._allowSubmitFiles.length === 0) {
            this.contextMenuConfig.items.push({ id: '1', text: StringLiteralsPipe.translate('general.submit'), disabled: true });
        } else {
            this.contextMenuConfig.items.push({ id: '1', text: StringLiteralsPipe.translate('general.submit'), disabled: false });
        }
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event.event, event.data));
    }
    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    @HostListener('window:resize', ['$event']) onResize(e) {
        if (!this._gridApi) {
            return;
        }

        this._gridApi.sizeColumnsToFit();
    }

    onSelectionChanged(event) {
        const index = event.rowIndex;
        if (event.event.shiftKey) { // shift first
            let firstSelectedEntryIndex = this.minIndex;
            let secondSelectedEntryIndex = this.maxIndex;
            if (index < firstSelectedEntryIndex) {
                firstSelectedEntryIndex = index;
            } else if (index > secondSelectedEntryIndex) {
                secondSelectedEntryIndex = index;
            }
            let entryIndex = -1;
            for (entryIndex = firstSelectedEntryIndex; entryIndex <= secondSelectedEntryIndex; ++entryIndex) {
                this.selectedIndexes.add(entryIndex);
            }
            this._refreshCells();
        } else if (event.event.ctrlKey || event.event.metaKey) { // handle command key in MAC book
            // select and de-select
            if (this.selectedIndexes.has(index)) {
                this.selectedIndexes.delete(index);
            } else {
                this.selectedIndexes.add(index);
            }
            const selectedIndexesArray = Array.from(this.selectedIndexes).sort();
            this.minIndex = selectedIndexesArray[0];
            this.maxIndex = selectedIndexesArray[selectedIndexesArray.length - 1];
            this._refreshCells();
        } else {
            this._refreshCells();
            this._changeSelect(index);
        }
        this._changeSelectStyle();
        this._selectedFiles = this.gridOptions.api.getSelectedRows();
        this._intralinkService.updateSelectedFiles(this._selectedFiles, this.files[0].parentId);
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;
        const sortModel = [
            { colId: 'createdDate', sort: 'desc' }
        ];
        this._columnApi.applyColumnState({ state: sortModel });
    }

    private _changeSelectStyle() {
        const selectedSet = this.selectedIndexes;
        this._gridApi.forEachNode(function (node) {
            if (selectedSet.has(node.rowIndex)) {
                node.setSelected(true);
            } else {
                node.setSelected(false);
            }
        });
    }

    private _changeSelect(index) {
        this.selectedIndexes.clear();
        this.minIndex = this.maxIndex = index;
        this.selectedIndexes.add(index);
    }

    private _gridColumnAutoFit() {
        if (this._gridApi) {
            this._gridApi.refreshClientSideRowModel();
            this._gridApi.sizeColumnsToFit();
        }
    }

    private _initGrid() {
        const cellStyleHandler = (params) => {
            // the font-size needs to be important, because the aacl-list's font-size has added the important for the font-size
            const styles = {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none',
                'font-size': '14px !important',
                'font-weight': '400',
            };

            if (params.data && !params.data.active) {
                return Object.assign(styles, { 'color': 'rgba(0, 0, 0, 0.85)' });
            } else {
                return styles;
            }
        };

        this.columnDefs = [{
            headerName: 'FILENAME',
            cellRenderer: IntralinksFolderNameComponent,
            valueGetter: (param) => param.data.name + param.data.extension,
            colId: 'name',
            minWidth: 160,
            width: 280,
            sortable: true,
            menuTabs: [],
            cellStyle: cellStyleHandler,
            onCellClicked: (param) => {
                this._fileOperate(param);
            },
        }, {
            headerName: 'SIZE',
            valueGetter: (param) => {
                return +(param.data && param.data.fileSize);
            },
            cellRenderer: (param) => {
                return param.value ? (param.value / 1024 > 1 ? Math.floor(param.value / 1024) + ' KB' : param.value + ' B') : '';
            },
            colId: 'fileSize',
            tooltipField: 'fileSize',
            minWidth: 80,
            width: 120,
            sortable: true,
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'TYPE',
            valueGetter: (param) => param.data && param.data.extension ? param.data.extension.replace('.', '').toUpperCase() : '',
            colId: 'mimeType',
            tooltipField: 'mimeType',
            minWidth: 80,
            width: 120,
            sortable: true,
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: 'DATE ADDED',
            valueGetter: 'data.createdDate',
            colId: 'createdDate',
            tooltipField: 'createdDate',
            minWidth: 120,
            width: 200,
            sortable: true,
            menuTabs: [],
            cellRenderer: (data) => {
                const datePipe = new DatePipe('en-US');
                return data.value ? datePipe.transform(data.value, 'MM/dd/yyyy HH:mm') : '';
            },
            cellStyle: cellStyleHandler
        }, {
            headerName: 'ADDED BY',
            valueGetter: 'data.createdBy',
            colId: 'createdBy',
            tooltipField: 'createdBy',
            minWidth: 120,
            width: 200,
            sortable: true,
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {

            headerName: 'TAMALE LINK',
            minWidth: 120,
            width: 200,
            cellRenderer: (params) => {
                if (params.data && params.data.isSubmitted && params.data.noteId && params.data.noteTitle) {
                    return '<a href="/tamaleweb/entry/' + params.data.noteId + '" target="_blank" title="' + params.data.noteTitle + '">' + params.data.noteTitle + '</a>';
                }
                return '';
            },
            sortable: false,
            suppressMovable: true,
            menuTabs: [],
            cellStyle: cellStyleHandler
        }, {
            headerName: '',
            minWidth: 120,
            width: 220,
            cellRenderer: IntralinksFolderActionsComponent,
            // cellRendererParams: {},
            sortable: false,
            suppressMovable: true,
            menuTabs: [],
        }];

        this.gridOptions = {
            debug: false,
            defaultColDef: {
                sortable: true,
                resizable: true
            },
            domLayout: this.autoHeight ? 'autoHeight' : 'normal',
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            suppressColumnVirtualisation: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'multiple',
            sortingOrder: ['desc',
                'asc'],
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            getRowHeight: () => 40,
            rowMultiSelectWithClick: true,
        };
    }

    private _fileOperate(param) {
        const url = AppConfig.intralinksEndPoint + 'workspaces/' + param.data.workspaceId + '/documents/' + param.data.id + '/file?screen-id=' + this._screenId + '&userid=' + this._authHelperService.getUserId() + '&file-name=' + encodeURIComponent(param.value);
        if (param.data && param.data.extension && param.data.extension.substr(1, param.data.extension.length).toLowerCase() === 'pdf') {
            // pdf
            this._fileHelperService.previewFile('', url, param.data.extension.substr(1, param.data.extension.length), param.value, true);
        } else if (param.data && param.data.extension) {
            // txt word
            this._fileHelperService.downloadFile$.next();
            this._fileHelperService.downloadFile(url, true);
        } else {
            this._router.navigate(['folder', param.data.id], { relativeTo: this._activatedRoute });
        }
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.forceRefresh = true;
        this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event));
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        const threadEntry = event.payload.data;
        switch (type) {
            case 1:
                this._submit();
                break;
            default:
                break;
        }
    }

    private _refreshCells() {
        // Because redrawing the row resets the isloading state of the button in the cell, change this to refresh the cell
        if (this._gridApi) {
            this._gridApi.refreshCells();
        }
    }

    private _submit() {
        const _noteOption = new NoteDialogOpenOptions();
        _noteOption.openType = NoteDialogType.CreateNote;
        _noteOption.minimizable = true;
        _noteOption.intralinks = this._allowSubmitFiles;
        if (this.allSelectedFiles.size === 1 || !this.isgroup) {
            if (this._allowSubmitFiles[0].mapping && this._allowSubmitFiles[0].mapping.noteType) {
                _noteOption.entryTypeId = this._allowSubmitFiles[0].mapping.noteType.id;
            }
        }
        if (_noteOption.intralinks != null) {
            this._noteDialogService.dialogOpen$.next(_noteOption);
        }

    }

}
